import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { parseISO, parse, isValid } from 'date-fns';

// ========================================
// Date Formats from BE admin2 controllers
// ========================================

/**
 * Date formats returned by BE admin2 controllers.
 * Order matters - try most specific formats first to avoid false positives.
 *
 * Reference: https://docs.w3cub.com/date_fns/parse
 */
const DATE_FORMATS = [
  "MMM d, yyyy 'at' h:mmaaa", // "Jan 4, 2021 at 3:19pm" - verbose datetime
  'yyyy-MM-dd HH:mm:ss', // "2021-01-04 15:19:00" - datetime without TZ
  'MM/dd/yyyy', // "01/04/2021" - US date format
  'yyyy-MM-dd', // "2021-01-04" - ISO date only
  'MMM d, yyyy', // "Jan 4, 2021" - short date fallback
];

// ========================================
// Types
// ========================================

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export interface UseSortableDataResult<T> {
  sortedData: T[];
  sortConfig: SortConfig;
  requestSort: (key: string) => void;
  getSortDirection: (key: string) => SortDirection;
}

// ========================================
// Hook: useSortableData
// ========================================

export function useSortableData<T>(
  data: T[],
  initialSortConfig: SortConfig = { key: '', direction: null }
): UseSortableDataResult<T> {
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSortConfig);

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // Handle different types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle numeric strings (e.g., IDs that come as strings from API)
      // Check if both values look like numbers (integers or decimals without extra chars)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const numericPattern = /^-?\d+(\.\d+)?$/;
        if (numericPattern.test(aValue.trim()) && numericPattern.test(bValue.trim())) {
          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
      }

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc'
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Try to parse as dates if they look like date strings
      const aDate = parseDate(aValue);
      const bDate = parseDate(bValue);
      if (aDate && bDate) {
        return sortConfig.direction === 'asc'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }

      // Default string comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  const requestSort = useCallback((key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Cycle through: asc -> desc -> null
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { key: '', direction: null };
        }
      }
      // New column or reset, start with ascending
      return { key, direction: 'asc' };
    });
  }, []);

  const getSortDirection = useCallback(
    (key: string): SortDirection => {
      if (sortConfig.key === key) {
        return sortConfig.direction;
      }
      return null;
    },
    [sortConfig]
  );

  return { sortedData, sortConfig, requestSort, getSortDirection };
}

// ========================================
// Helper Functions
// ========================================

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

/**
 * Parses a value as a date using date-fns.
 * Handles all date formats returned by BE admin2 controllers.
 *
 * @param value - The value to parse (expected to be a date string)
 * @returns Date object if parsing succeeds, null otherwise
 */
function parseDate(value: any): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;

  // 1. Try ISO8601 first (most common - BE uses .iso8601 for most timestamps)
  //    Handles: "2021-01-04T15:19:00-05:00", "2021-01-04T15:19:00Z"
  const isoDate = parseISO(value);
  if (isValid(isoDate)) return isoDate;

  // 2. Try known strftime formats from BE controllers
  for (const formatStr of DATE_FORMATS) {
    try {
      const parsed = parse(value, formatStr, new Date());
      if (isValid(parsed)) return parsed;
    } catch {
      // Continue to next format
    }
  }

  return null;
}

// ========================================
// Styled Components
// ========================================

export const SortableHeaderCell = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#666')};
  border-bottom: 1px solid #e9ecef;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

export const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  display: inline-block;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
    font-size: 12px;
  }
`;

// ========================================
// SortableHeader Component
// ========================================

interface SortableHeaderProps {
  label: string;
  sortKey: string;
  sortDirection: SortDirection;
  onSort: (key: string) => void;
  sortable?: boolean;
}

export const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  sortDirection,
  onSort,
  sortable = true,
}) => {
  return (
    <SortableHeaderCell
      $sortable={sortable}
      $sorted={sortDirection !== null}
      onClick={() => sortable && onSort(sortKey)}
    >
      {label}
      {sortable && <SortIndicator $direction={sortDirection} />}
    </SortableHeaderCell>
  );
};

export default useSortableData;
